import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { IconButton, LinearProgress } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { Button } from 'core/components/button';
import { NotificationCard } from 'core/components/notification-card';
import darkInfoIcon from 'core/assets/svg/ic-info-black.svg';
import { uploadTaskDocument } from 'customer/helpers/policy-document-service';
import { onboardingTasks, taskDocumentType } from '@ourbranch/onboarding-tasks';

import DocumentUpload from './document-upload';
import DocumentList from './document-list';
import useStyles from './document-modal.styles';

//  currently just being used for the task center documents upload,
// but will be refactored to be used for as reusbale component
const getDocName = (taskName, policyData) => {
  const { policyType, policyId } = policyData;
  const now = new Date();
  switch (taskName) {
    case onboardingTasks.SignApplication:
      return `${now.getFullYear()}-${
        now.getMonth() + 1
      }-${now.getDate()}-${now.getHours()}-${now.getMinutes()}-${policyType[0].toUpperCase()}_APPL`;
    case onboardingTasks.CompleteInventory:
      return `inventory_${policyId}`;
    case onboardingTasks.ConnectedHomeRenewal:
      return `connected_home_renewal_${policyId}`;
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return `proof_of_advanced_windstorm_mitigation_${policyId}`;
    case onboardingTasks.VerifyRoofYear:
      return `verify_roof_year_${policyId}`;
    default:
      return null;
  }
};

const mapDocuments = (docs = []) => {
  return docs
    .map((document) => {
      const id = document.id || document.documentUrl || document.path;
      if (!id) {
        return null;
      }
      const fileName = document.fileName || document.name || id.split('/').pop();
      const uploadedAt = document.uploadedAt || document.lastModified || document.createdAt || null;
      const documentUrl = document.documentUrl || document.path || id;
      return { id, fileName, uploadedAt, documentUrl };
    })
    .filter(Boolean);
};

const DocumentModal = ({
  open,
  onClose,
  documents,
  taskId,
  accountId,
  onUpdateTaskData,
  completeTask,
  taskName,
  policyId,
  data,
  driver,
  documentType,
  isAgency,
  processingReviewTasks,
  appCuesOnClick
}) => {
  const classes = useStyles();
  const toast = useToast();
  const { viewOnly } = useSession();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [displayDocuments, setDisplayDocuments] = useState(() => mapDocuments(documents));
  const [, type] = policyId.split('-');

  useEffect(() => {
    if (open) {
      setDisplayDocuments(mapDocuments(documents));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateTaskDocuments = useCallback(
    async (documents) => {
      if (taskId && accountId && onUpdateTaskData) {
        try {
          const files = documents.map((doc) => ({
            documentUrl: doc.documentUrl || doc.id,
            fileName: doc.fileName,
            uploadedAt: doc.uploadedAt
          }));
          const updatedData = {
            ...data,
            files
          };

          await onUpdateTaskData(taskId, accountId, updatedData);
        } catch (error) {
          toast.notify({
            type: 'error',
            message: 'Failed to update documents. Please try again.'
          });
          throw error;
        }
      }
    },
    [taskId, accountId, onUpdateTaskData, toast, data]
  );

  const handleUpload = useCallback(
    async (fileOrFiles) => {
      const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
      const isSingleFile = files.length === 1;
      const totalFiles = files.length;
      const succeeded = [];
      const failed = [];

      setIsUploading(true);
      setProgress(0);

      let fileIndex = 0;
      for (const file of files) {
        const fileNameBase =
          getDocName(taskName, { policyType: type, policyId }) || `${taskName}${driver ? ` - ${driver}` : ''}`;
        const splitName = file.name.split('.');
        const ext = splitName[splitName.length - 1];
        const uniqueId = uuidv4();
        const normalizedFileName = fileNameBase ? `${fileNameBase}_${uniqueId}.${ext}` : file.name;

        try {
          let path = '';

          if (documentType === taskDocumentType.Internal) {
            path = `internal/${accountId}/${normalizedFileName}`;
          } else {
            path = `docs/${accountId}/customer/${policyId}/${normalizedFileName}`;
          }

          const uploadedDoc = await uploadTaskDocument(accountId, file, path, (progressEvent) => {
            const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
            if (isSingleFile) {
              setProgress(fileProgress);
            } else {
              const completedProgress = (fileIndex / totalFiles) * 100;
              const currentProgress = fileProgress / totalFiles;
              setProgress(completedProgress + currentProgress);
            }
          });

          const docToMap = {
            ...(uploadedDoc || {}),
            documentUrl: uploadedDoc?.documentUrl || uploadedDoc?.path || uploadedDoc?.id || uploadedDoc,
            fileName: normalizedFileName,
            uploadedAt: uploadedDoc?.uploadedAt || new Date().toISOString()
          };

          const mappedDoc = mapDocuments([docToMap])[0];

          if (mappedDoc) {
            succeeded.push(mappedDoc);
          } else {
            failed.push(file.name);
          }
        } catch {
          failed.push(file.name);
        }

        fileIndex++;
      }

      if (succeeded.length > 0) {
        setDisplayDocuments((prevDocuments) => {
          const newDocuments = [...prevDocuments, ...succeeded];
          updateTaskDocuments(newDocuments);
          return newDocuments;
        });
      }

      setIsUploading(false);
      setProgress(0);

      if (failed.length === 0) {
        toast.notify({
          type: 'success',
          message: `Successfully uploaded ${succeeded.length} file(s)`
        });
      } else if (succeeded.length > 0 && failed.length > 0) {
        toast.notify({
          type: 'error',
          message: `Uploaded ${succeeded.length} file(s) successfully. Failed: ${failed.join(', ')}`
        });
      } else {
        toast.notify({
          type: 'error',
          message: 'Failed to upload document(s). Please try again.'
        });
      }
    },
    [taskName, type, policyId, driver, documentType, accountId, updateTaskDocuments, toast]
  );

  const handleDeleteDocument = useCallback(
    async (documentId) => {
      setDisplayDocuments((prevDocuments) => {
        const newDocuments = prevDocuments.filter((doc) => doc.id !== documentId);
        updateTaskDocuments(newDocuments);
        return newDocuments;
      });
    },
    [updateTaskDocuments]
  );

  const handleClose = useCallback(() => {
    if (displayDocuments.length > 0) {
      setShowCloseConfirmation(true);
    } else {
      onClose();
    }
  }, [displayDocuments.length, onClose]);

  const onSubmit = async () => {
    if (!displayDocuments.length) {
      toast.notify({
        type: 'error',
        message: 'You need to upload at least one document before submitting the task.'
      });
      return;
    }

    try {
      const files = displayDocuments.map((doc) => ({
        documentUrl: doc.documentUrl,
        fileName: doc.fileName,
        uploadedAt: doc.uploadedAt
      }));

      const res = await completeTask({
        taskId,
        accountId,
        files,
        policyId,
        taskName,
        data,
        submittedByIA: isAgency
      });

      if (res?.ok) {
        const toastMessage =
          isAgency && processingReviewTasks.includes(taskName)
            ? `The ${taskName} task ${driver ? `for ${driver} ` : ''}has been submitted. Branch will review them within 2 to 3 days and notify you if there are issues with the document.`
            : `${taskName} ${driver ? `for ${driver} ` : ''}has been marked complete and ${displayDocuments.length} document(s) uploaded to ${
                documentType ? documentType.toLowerCase() : ''
              } documents.`;

        toast.notify({
          type: 'success',
          message: toastMessage
        });
        onClose();
      } else {
        const errorMessage = `There was an error completing the task${res?.error ? ` - ${res.error}` : ''}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      toast.notify({
        type: 'error',
        message: error.message || 'There was an error completing the task'
      });
    }
  };

  // Close confirmation modal
  const handleCloseAnyway = useCallback(() => {
    setShowCloseConfirmation(false);
    onClose();
  }, [onClose]);

  const handleGoBack = useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  return (
    <>
      <BaseDialog open={open} onClose={handleClose} size="md" classes={{ paper: classes.container }}>
        <Label type="greenBig" className={classes.modalHeader}>
          Submit documents of the "{taskName}" task.
        </Label>
        <Label type="darkGreenSmall" className={classes.modalContent}>
          <div>
            Please upload all required documents for this task. See the task{' '}
            <IconButton className={classes.link} onClick={appCuesOnClick} color="secondary" type="button">
              details and requirements
            </IconButton>{' '}
            for more information.
          </div>
        </Label>

        <div className={classes.content}>
          <Card className={classes.documentsCard}>
            <DocumentList documents={displayDocuments} onDelete={handleDeleteDocument} loading={isUploading} />
            {!viewOnly && <DocumentUpload onUpload={handleUpload} loading={isUploading} />}
          </Card>
        </div>

        {isUploading && <LinearProgress color="secondary" variant="determinate" value={progress} />}

        <Grid className={classes.btnContainer}>
          <Button variant="contained" color="secondary" className={classes.noBtn} onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" className={classes.yesBtn} onClick={onSubmit}>
            Submit task for review
          </Button>
        </Grid>
      </BaseDialog>

      {/* Confirmation modal when closing with unsaved work */}
      <Dialog open={showCloseConfirmation} onClose={handleGoBack} classes={{ paper: classes.confirmModalContainer }}>
        <DialogTitle>
          <Label type="greenBig" className={classes.modalHeader}>
            Close without submitting?
          </Label>
        </DialogTitle>

        <DialogContent className={classes.content}>
          <Label type="darkGreenSmall" className={classes.modalContent}>
            You've uploaded files to this task, but haven't submitted them yet. If you exit now, the files will stay
            attached and be visible to others who open this task.
          </Label>

          <NotificationCard
            type="infoGreen"
            header="Documents won't be reviewed until the task is submitted."
            iconByHeader
            iconSize="icon16"
            icon={darkInfoIcon}
            iconClassName={classes.icon}
            noShadow
          />

          <Grid className={classes.btnContainer}>
            <Button variant="contained" color="secondary" className={classes.noBtn} onClick={handleCloseAnyway}>
              Close anyway
            </Button>
            <Button variant="contained" color="primary" className={classes.submitBtn} onClick={handleGoBack}>
              Go back and submit
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

DocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  taskId: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  onUpdateTaskData: PropTypes.func.isRequired,
  completeTask: PropTypes.func.isRequired,
  taskName: PropTypes.string.isRequired,
  policyId: PropTypes.string.isRequired,
  data: PropTypes.object,
  driver: PropTypes.string,
  documentType: PropTypes.string.isRequired,
  isAgency: PropTypes.bool,
  processingReviewTasks: PropTypes.arrayOf(PropTypes.string).isRequired
};

DocumentModal.defaultProps = {
  data: null,
  driver: null,
  isAgency: false
};

export default DocumentModal;
